/**
 * WebSocket session client.
 *
 * Owns one socket for the lifetime of a session and turns the server's event
 * stream into typed callbacks. Everything the UI needs is here: reconnect with
 * exponential backoff and jitter, a heartbeat that detects a half-open socket,
 * request/response correlation for the REST-like events, and a monotonically
 * increasing sequence check so a late frame from a previous connection is
 * dropped rather than rendered out of order.
 *
 * Audio is sent as binary frames with a one-byte speaker header, which is what
 * keeps the per-frame cost at a context switch instead of a JSON round trip.
 */

import { LIMITS } from "@shared/constants";
import type { SpeakerId } from "@shared/constants";
import type { AudioOutputMode, ConversationMode, SessionState } from "@shared/constants";
import type { LanguageCode, LanguageDetection } from "@shared/types/language";
import { parseServerEvent, type FinalUtterance, type ServerEvent, type TranslationResult } from "@shared/types/protocol";
import type { TranscriptEntry } from "@shared/types/transcript";
import type { ProviderHealth } from "@shared/types/provider";
import type { DreamTongueApi } from "./api";

/** Binary frame header: one byte speaker index, then linear16 mono PCM. */
const SPEAKER_HEADER_BYTES = 1;
const SPEAKER_INDEX: Readonly<Record<SpeakerId, number>> = { A: 0, B: 1, C: 2, D: 3 };

export interface SessionConfig {
  readonly sourceLanguage: LanguageCode | "auto";
  readonly targetLanguage: LanguageCode;
  readonly mode: ConversationMode;
  readonly speakers: readonly SpeakerId[];
  readonly audioOutput: AudioOutputMode;
  readonly ttsEnabled: boolean;
  readonly persistTranscript: boolean;
}

export interface ReadyInfo {
  readonly sessionId: string;
  readonly protocolVersion: number;
  readonly maxSessionSeconds: number;
  readonly remainingSeconds: number;
  readonly limits: Readonly<Record<string, number>>;
  readonly providers: readonly ProviderHealth[];
}

export interface SessionHandlers {
  onState: (state: SessionState, detail?: string) => void;
  onReady: (info: ReadyInfo) => void;
  onPartial: (input: { speaker: SpeakerId; text: string; elapsedMs: number; confidence: number | null }) => void;
  onFinal: (utterance: FinalUtterance) => void;
  onTranslationPartial: (input: { utteranceId: string; speaker: SpeakerId; text: string; sourceLanguage: LanguageCode; targetLanguage: LanguageCode }) => void;
  onDetected: (detection: LanguageDetection, override: boolean) => void;
  onTtsChunk: (input: { utteranceId: string; audio: ArrayBuffer }) => void;
  onTtsStarted: (utteranceId: string, language: LanguageCode) => void;
  onTtsCompleted: (utteranceId: string) => void;
  onEntry: (entry: TranscriptEntry) => void;
  onCleared: () => void;
  onPersisted: (transcriptId: string | null) => void;
  onQuota: (remaining: number, resetsAt: number, reason: string) => void;
  onError: (code: string, message: string, retryable: boolean) => void;
}

type Pending = { resolve: (value: unknown) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> };

export class SessionClient {
  #api: DreamTongueApi;
  #handlers: SessionHandlers;
  #config: SessionConfig;

  #socket: WebSocket | null = null;
  #state: SessionState = "idle";
  #closing = false;
  #attempt = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  #lastSeq = 0;
  #pending = new Map<string, Pending>();
  #sessionId: string | null = null;
  #sessionToken: string | null = null;

  constructor(api: DreamTongueApi, config: SessionConfig, handlers: SessionHandlers) {
    this.#api = api;
    this.#config = config;
    this.#handlers = handlers;
  }

  get state(): SessionState {
    return this.#state;
  }

  get sessionId(): string | null {
    return this.#sessionId;
  }

  get isConnected(): boolean {
    return this.#socket?.readyState === WebSocket.OPEN;
  }

  // =========================================================================
  //  Connection
  // =========================================================================

  async connect(): Promise<void> {
    this.#closing = false;
    this.#setState("connecting");

    const created = await this.#api.createSession({
      sourceLanguage: this.#config.sourceLanguage,
      targetLanguage: this.#config.targetLanguage,
      mode: this.#config.mode,
      persistTranscript: this.#config.persistTranscript,
    });

    if (!created.ok) {
      this.#setState("error", created.error.message);
      this.#handlers.onError(created.error.code, created.error.message, created.error.retryable);
      throw new Error(created.error.message);
    }

    this.#sessionId = created.value.sessionId;
    this.#sessionToken = created.value.sessionToken;
    this.#openSocket(created.value.websocketUrl);
  }

  #openSocket(path: string): void {
    // The socket lives wherever the API does, not wherever the page does. In a
    // split deployment the SPA is on Pages and the Worker is on workers.dev, and
    // resolving against `window.location.host` would try to open a WebSocket on
    // a Pages origin that serves only static files.
    const base = this.#api.baseUrl;
    const url = base
      ? new URL(path, base)
      : new URL(path, `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`);

    if (url.protocol === "https:") url.protocol = "wss:";
    else if (url.protocol === "http:") url.protocol = "ws:";
    url.searchParams.set("token", this.#sessionToken ?? "");

    const socket = new WebSocket(url.toString());
    socket.binaryType = "arraybuffer";
    this.#socket = socket;

    socket.onopen = () => {
      this.#attempt = 0;
      this.#lastSeq = 0;
      this.#setState("connected");
      this.#sendConfig();
      this.#startHeartbeat();
    };

    socket.onmessage = (event: MessageEvent) => {
      if (typeof event.data !== "string") return;
      this.#onMessage(event.data);
    };

    socket.onerror = () => {
      // `onclose` always follows; the status is logged there with more context.
    };

    socket.onclose = (event) => {
      this.#stopHeartbeat();
      this.#rejectAllPending("The connection closed.");
      this.#socket = null;

      if (this.#closing) {
        this.#setState("closed");
        return;
      }

      // 1000 is a clean close we asked for; anything else may be a deploy.
      this.#setState("error", `Disconnected (${event.code})`);
      this.#scheduleReconnect();
    };
  }

  /**
   * Reconnect with exponential backoff and full jitter.
   *
   * Jitter matters: without it, a Cloudflare deploy drops every open socket at
   * the same instant and every client retries in lockstep.
   */
  #scheduleReconnect(): void {
    if (this.#closing) return;
    this.#attempt += 1;
    if (this.#attempt > 6) {
      this.#setState("error", "Could not reconnect. Reload to start a new session.");
      this.#handlers.onError("network", "Lost the connection and could not restore it.", false);
      return;
    }

    const exponential = Math.min(LIMITS.RECONNECT_MAX_MS, LIMITS.RECONNECT_MIN_MS * 2 ** (this.#attempt - 1));
    const delay = Math.round(exponential * (0.5 + Math.random() * 0.5));

    this.#reconnectTimer = setTimeout(() => {
      void this.#reconnect();
    }, delay);
  }

  async #reconnect(): Promise<void> {
    // The Durable Object keeps the transcript, so reattaching recovers context
    // rather than starting over. A new session id means the old one is gone.
    if (!this.#sessionId || !this.#sessionToken) {
      await this.connect().catch((error: unknown) => {
        this.#handlers.onError("network", String(error), true);
      });
      return;
    }
    this.#api.setCredentials({ sessionId: this.#sessionId, sessionToken: this.#sessionToken });
    this.#openSocket(`/api/ws?session=${encodeURIComponent(this.#sessionId)}`);
  }

  async disconnect(): Promise<void> {
    this.#closing = true;
    this.#stopHeartbeat();
    if (this.#reconnectTimer !== null) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }

    this.#send({ type: "session.end" });
    this.#socket?.close(1000, "Client closed the session");
    this.#socket = null;

    if (this.#sessionId) {
      await this.#api.endSession().catch(() => undefined);
    }
    this.#sessionId = null;
    this.#sessionToken = null;
    this.#setState("closed");
  }

  // =========================================================================
  //  Outbound
  // =========================================================================

  /** Send one PCM frame for `speaker`. No-op when the socket is not open. */
  sendAudio(speaker: SpeakerId, pcm: Int16Array): void {
    const socket = this.#socket;
    if (!socket || socket.readyState !== WebSocket.OPEN || pcm.length === 0) return;

    const buffer = new ArrayBuffer(SPEAKER_HEADER_BYTES + pcm.length * 2);
    new Uint8Array(buffer, 0, 1)[0] = SPEAKER_INDEX[speaker] ?? 0;
    const view = new DataView(buffer, SPEAKER_HEADER_BYTES);
    for (let i = 0; i < pcm.length; i += 1) view.setInt16(i * 2, pcm[i] ?? 0, true);

    try {
      socket.send(buffer);
    } catch {
      // A full send buffer is backpressure; dropping a frame is correct.
    }
  }

  startAudio(speaker: SpeakerId, sampleRate: number): void {
    this.#send({ type: "audio.start", speaker, sampleRate, encoding: "linear16" });
  }

  stopAudio(speaker: SpeakerId): void {
    this.#send({ type: "audio.stop", speaker });
  }

  flushAudio(speaker: SpeakerId): void {
    this.#send({ type: "audio.flush", speaker });
  }

  changeLanguages(sourceLanguage: LanguageCode | "auto", targetLanguage: LanguageCode): void {
    this.#config = { ...this.#config, sourceLanguage, targetLanguage };
    this.#send({ type: "language.change", sourceLanguage, targetLanguage });
  }

  updateSettings(settings: Pick<SessionConfig, "audioOutput" | "ttsEnabled" | "persistTranscript">): void {
    this.#config = { ...this.#config, ...settings };
    this.#send({ type: "settings.update", ...settings });
  }

  resetConversation(): void {
    this.#send({ type: "conversation.reset" });
  }

  /**
   * Tell the server which language this speaker talks.
   *
   * The server routes each turn into the *other* speaker's language, so a
   * two-way conversation only works if both sides are declared. Without it every
   * turn would be translated into the session-wide target and both people would
   * end up reading the same language.
   */
  assignSpeaker(speaker: SpeakerId, language: LanguageCode | "auto"): void {
    this.#send({ type: "speaker.assign", speaker, language });
  }

  /** Await a `translation.result`, `detection.result` or `tts.result`. */
  request<T>(build: (requestId: string) => Record<string, unknown>): Promise<T> {
    const requestId = `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.#pending.delete(requestId);
        reject(new Error("The server did not respond in time."));
      }, 30_000);
      this.#pending.set(requestId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
      });
      const payload = build(requestId);
      const ok = this.#send(payload as never);
      if (!ok) {
        clearTimeout(timer);
        this.#pending.delete(requestId);
        reject(new Error("The session is not connected."));
      }
    });
  }

  /** Synchronous translation without a request/response round trip. */
  translateNow(text: string, sourceLanguage: LanguageCode | "auto", targetLanguage: LanguageCode): Promise<TranslationResult[]> {
    return this.request<TranslationResult[]>((requestId) => ({
      type: "translation.request",
      texts: [text],
      sourceLanguage,
      targetLanguage,
      requestId,
    }));
  }

  detectNow(text: string): Promise<LanguageDetection> {
    return this.request<LanguageDetection>((requestId) => ({ type: "detection.request", text, requestId }));
  }

  // =========================================================================
  //  Inbound
  // =========================================================================

  #onMessage(raw: string): void {
    const event = parseServerEvent(raw);
    if (!event) return;

    // Drop anything from a previous connection so the UI never re-renders
    // stale text after a reconnect.
    if (event.seq > 0 && event.seq <= this.#lastSeq) return;
    if (event.seq > 0) this.#lastSeq = event.seq;

    this.#dispatch(event);
  }

  #dispatch(event: ServerEvent): void {
    switch (event.type) {
      case "session.ready":
        this.#setState(event.state);
        this.#handlers.onReady({
          sessionId: event.sessionId,
          protocolVersion: event.protocolVersion,
          maxSessionSeconds: event.maxSessionSeconds,
          remainingSeconds: event.remainingSeconds,
          limits: event.limits,
          providers: event.providers,
        });
        return;

      case "session.state":
        this.#setState(event.state, event.detail);
        return;

      case "transcription.partial":
        this.#handlers.onPartial({
          speaker: event.speaker,
          text: event.text,
          elapsedMs: event.elapsedMs,
          confidence: event.confidence,
        });
        return;

      case "transcription.final":
      case "translation.final":
        this.#handlers.onFinal(event.utterance);
        return;

      case "translation.partial":
        this.#handlers.onTranslationPartial({
          utteranceId: event.utteranceId,
          speaker: event.speaker,
          text: event.text,
          sourceLanguage: event.sourceLanguage,
          targetLanguage: event.targetLanguage,
        });
        return;

      case "language.detected":
        this.#handlers.onDetected(event.detection, event.override);
        return;

      case "tts.started":
        this.#handlers.onTtsStarted(event.utteranceId, event.language);
        return;

      case "tts.chunk": {
        const audio = base64ToArrayBuffer(event.base64);
        if (audio.byteLength > 0) this.#handlers.onTtsChunk({ utteranceId: event.utteranceId, audio });
        if (event.final) this.#handlers.onTtsCompleted(event.utteranceId);
        return;
      }

      case "tts.completed":
        this.#handlers.onTtsCompleted(event.utteranceId);
        return;

      case "transcript.entry":
        this.#handlers.onEntry(event.entry);
        return;

      case "transcript.cleared":
        this.#handlers.onCleared();
        return;

      case "transcript.persisted":
        this.#handlers.onPersisted(event.transcriptId);
        return;

      case "quota":
        this.#handlers.onQuota(event.remaining, event.resetsAt, event.reason);
        return;

      case "translation.result":
        this.#settle(event.requestId, event.results);
        return;

      case "detection.result":
        this.#settle(event.requestId, event.detection);
        return;

      case "tts.result":
        // The envelope carries base64; a raw ArrayBuffer cannot survive JSON.
        this.#settle(event.requestId, base64ToArrayBuffer(event.base64));
        return;

      case "pong":
        return;

      case "ping":
        this.#send({ type: "ping" });
        return;

      case "error":
        if (event.requestId) this.#fail(event.requestId, new Error(event.message));
        else this.#handlers.onError(event.code, event.message, event.retryable);
        if (event.code === "session_expired" || event.code === "quota_exceeded") this.#setState("error", event.message);
        return;

      default:
        return;
    }
  }

  // =========================================================================
  //  Plumbing
  // =========================================================================

  #sendConfig(): void {
    this.#send({
      type: "session.start",
      sourceLanguage: this.#config.sourceLanguage,
      targetLanguage: this.#config.targetLanguage,
      mode: this.#config.mode,
      audioOutput: this.#config.audioOutput,
      ttsEnabled: this.#config.ttsEnabled,
      speakers: this.#config.speakers,
      persistTranscript: this.#config.persistTranscript,
    });
  }

  #send(event: Record<string, unknown>): boolean {
    const socket = this.#socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(JSON.stringify({ ...event, ts: Date.now() }));
      return true;
    } catch {
      return false;
    }
  }

  #settle(requestId: string, value: unknown): void {
    const pending = this.#pending.get(requestId);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.#pending.delete(requestId);
    pending.resolve(value);
  }

  #fail(requestId: string, error: Error): void {
    const pending = this.#pending.get(requestId);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.#pending.delete(requestId);
    pending.reject(error);
  }

  #rejectAllPending(reason: string): void {
    for (const [, pending] of this.#pending) {
      clearTimeout(pending.timer);
      pending.reject(new Error(reason));
    }
    this.#pending.clear();
  }

  /**
   * Heartbeat.
   *
   * Browsers silently drop half-open sockets without firing `onclose`, so the
   * only reliable liveness check is an application-level ping. A missed pong
   * means the connection is dead even though `readyState` still says OPEN.
   */
  #startHeartbeat(): void {
    this.#stopHeartbeat();
    this.#heartbeatTimer = setInterval(() => {
      const socket = this.#socket;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      this.#send({ type: "ping" });
    }, LIMITS.HEARTBEAT_MS);
  }

  #stopHeartbeat(): void {
    if (this.#heartbeatTimer !== null) {
      clearInterval(this.#heartbeatTimer);
      this.#heartbeatTimer = null;
    }
  }

  #setState(state: SessionState, detail?: string): void {
    if (this.#state === state && !detail) return;
    this.#state = state;
    this.#handlers.onState(state, detail);
  }
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  } catch {
    return new ArrayBuffer(0);
  }
}
